import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import { ArrowPathIcon, ClipboardDocumentListIcon } from '@heroicons/react/24/outline';
import { auditService } from '../../services/system/audit';
import AuditEntryRow from './AuditEntryRow';

/**
 * Reusable per-record audit history.
 * Drop into a detail page tab:
 *
 *   <AuditActivityTab module={AUDIT_MODULES.TRIP} recordId={shipment.id} />
 *
 * Pulls company-scoped entries and filters to this record on the client.
 */
const AuditActivityTab = ({ module, recordId, title, hideHeader = false }) => {
  const { t } = useTranslation();
  const { currentCompany } = useAuth();
  const [loading, setLoading] = useState(false);
  const [entries, setEntries] = useState([]);
  const [error, setError] = useState(null);

  const load = async () => {
    if (!currentCompany?.id || !recordId) return;
    setLoading(true);
    setError(null);
    try {
      const data = await auditService.getActivityForRecord(currentCompany.id, { module, recordId, max: 200 });
      setEntries(data);
    } catch (err) {
      console.error('Error loading record audit history', err);
      setError(err.message || 'Failed to load history');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [currentCompany?.id, recordId, module]);

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-4">
      {!hideHeader && (
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-base font-semibold text-gray-900 flex items-center gap-2">
            <ClipboardDocumentListIcon className="w-5 h-5 text-blue-600" />
            {title || t('audit.activity_history', 'Activity / History')}
          </h3>
          <button
            onClick={load}
            disabled={loading}
            className="inline-flex items-center gap-1 text-xs text-gray-600 hover:text-gray-900 disabled:opacity-50"
          >
            <ArrowPathIcon className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            {t('common.refresh', 'Refresh')}
          </button>
        </div>
      )}

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded mb-3 text-sm">
          {error}
        </div>
      )}

      <div className="overflow-x-auto border border-gray-100 rounded">
        <table className="min-w-full text-sm">
          <thead className="bg-gray-50 border-b border-gray-200">
            <tr>
              <th className="px-3 py-2 w-8"></th>
              <th className="px-3 py-2 text-start font-medium text-gray-600">{t('audit.col.when', 'When')}</th>
              <th className="px-3 py-2 text-start font-medium text-gray-600">{t('audit.col.action', 'Action')}</th>
              <th className="px-3 py-2 text-start font-medium text-gray-600">{t('audit.col.record', 'Record')}</th>
              <th className="px-3 py-2 text-start font-medium text-gray-600">{t('audit.col.who', 'Who')}</th>
              <th className="px-3 py-2 text-start font-medium text-gray-600">{t('audit.col.tags', 'Tags')}</th>
            </tr>
          </thead>
          <tbody>
            {loading && entries.length === 0 && (
              <tr><td colSpan={6} className="px-3 py-4 text-center text-gray-500">{t('common.loading', 'Loading…')}</td></tr>
            )}
            {!loading && entries.length === 0 && (
              <tr><td colSpan={6} className="px-3 py-4 text-center text-gray-500">{t('audit.empty_record', 'No history yet for this record.')}</td></tr>
            )}
            {entries.map(e => (
              <AuditEntryRow key={e.id} entry={e} showModule={false} />
            ))}
          </tbody>
        </table>
      </div>

      <div className="mt-3 text-xs text-gray-400 italic">
        {t('audit.append_only_note', 'Audit entries are read-only and append-only — they cannot be edited or deleted from the application.')}
      </div>
    </div>
  );
};

export default AuditActivityTab;
